import { FileImageItem } from '@core/features';
import {
  FacebookPage,
  DefendResponseType,
  GooglePhotoAlbum,
  GoogleReview,
  Inquiry,
  LocalLocation,
  Review,
  ReviewRequest,
  SocialPost,
  StudioPhoto,
} from '@core/models';
import { WidgetConfig } from '@core/models/widget';
import { Merchant } from 'square';
import axios from 'axios';

export type AskForReviewRequestPayload = {
  contact: string;
  content: string;
  phone?: string;
  followUpContent: string;
  fullName: string;
  isDraft?: boolean;
};

export type UpsertAnonymousReviewPayload = {
  locationId: string;
  comment: string;
  fullName: string;
  emailAddress: string;
  rating: number;
};

export type UpdateLocationOwnerPayload = {
  ownerGivenName: string;
  ownerFamilyName: string;
  ownerEmail: string;
};

export default class HeroApiService {
  private url = `${process.env.REACT_APP_GOOGLE_OAUTH_ENDPOINT}/api`;

  public async generateResponse(reviewId: number, numberOfCompletions: number) {
    try {
      const response = await axios.post(
        `${this.url}/reviews/${reviewId}/generate`,
        {
          numberOfCompletions,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async generateInquiryResponse(reviewId: number) {
    try {
      const response = await axios.post(`${this.url}/reviews/${reviewId}/generateInquiry`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getCurrentUser() {
    try {
      const response = await axios.get(`${this.url}/users/me`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async logout() {
    try {
      const response = await axios.get(`${this.url}/auth/logout`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getAllLocations() {
    try {
      const response = await axios.get(`${this.url}/business/locations`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getLocationById(locationId: string): Promise<LocalLocation> {
    try {
      const response = await axios.get(`${this.url}/location/${locationId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getLocationPlaceId(locationId: string) {
    try {
      const response = await axios.get(`${this.url}/location/${locationId}/placeId`);
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async updateAutoPilot(locationId: string, autoEngage: boolean, autoRequest: boolean) {
    try {
      const response = await axios.put(
        `${this.url}/location/${locationId}/pilot`,
        {
          autoEngage: autoEngage,
          autoRequest: autoRequest,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getGoogleReviewsByLocation(
    locationId: string,
    pageSize?: number,
    pageToken?: string,
    orderBy?: 'updateTime desc' | 'rating desc' | 'updateTime' | 'rating'
  ) {
    try {
      const response = await axios.get(
        `${this.url}/reviews/locations/${locationId}?pageSize=${pageSize || 10}&pageToken=${
          pageToken || ''
        }&orderBy=${orderBy}`,
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getGoogleReviewById(locationId: string, reviewId: string) {
    try {
      const response = await axios.get(`${this.url}/reviews/locations/${locationId}/${reviewId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }
  public async getLocalReviewByGoogleId(reviewId: string): Promise<Review> {
    try {
      const response = await axios.get(`${this.url}/reviews/${reviewId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async saveReview(review: GoogleReview | Review, existed: boolean) {
    try {
      const response = await axios.post(
        `${this.url}/reviews/save`,
        { review: review, existed: existed },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async saveReviewAnonymous({
    comment,
    emailAddress,
    fullName,
    locationId,
    rating,
  }: UpsertAnonymousReviewPayload) {
    try {
      const response = await axios.post(
        `${this.url}/reviews/locations/${locationId}/anonymous/save`,
        {
          comment,
          emailAddress,
          fullName,
          rating,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getAllAnonymousReviews(locationId: string) {
    try {
      const response = await axios.post(`${this.url}/reviews/locations/${locationId}/anonymous/`);
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async updateOwner(locationId: string, payload: UpdateLocationOwnerPayload) {
    try {
      const response = await axios.post(`${this.url}/location/${locationId}/contact`, payload, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async openRequestEmail(reviewId: number) {
    try {
      const response = await axios.post(`${this.url}/requests/${reviewId}/open`, {});
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async respondRequest(requestId: number, rate: number, feedback: string | undefined) {
    try {
      const response = await axios.post(`${this.url}/requests/${requestId}/respond`, {
        rate,
        feedback,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async upsertReply(locationId: string, reviewId: string, message: string) {
    try {
      const response = await axios.post(
        `${this.url}/reviews/locations/${locationId}/${reviewId}/reply`,
        { message: message },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getReviewRequestById(requestId: number): Promise<ReviewRequest> {
    try {
      const response = await axios.get(`${this.url}/requests/${requestId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async deleteRequestById(requestId: number) {
    try {
      const response = await axios.delete(`${this.url}/requests/${requestId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getAllReviewRequests(locationId: string) {
    try {
      const response = await axios.get(`${this.url}/requests/locations/${locationId}/`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async askForReviewRequest(locationId: string, payload: AskForReviewRequestPayload) {
    try {
      const response = await axios.post(`${this.url}/requests/locations/${locationId}/`, payload, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async resendEmail(requestId: number, requestType: string) {
    try {
      const response = await axios.post(
        `${this.url}/requests/${requestId}/resend/${requestType}`,
        {},
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async publishDraft(requestId: number) {
    try {
      const response = await axios.post(
        `${this.url}/requests/${requestId}/publishDraft`,
        {},
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async selectLocation(
    accountId: string,
    locationId: string,
    title: string,
    fullAddress: string,
    primaryCategory: string
  ) {
    try {
      const response = await axios.post(
        `${this.url}/location/save`,
        {
          accountId,
          locationId,
          title,
          fullAddress,
          primaryCategory,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getSelectedLocation() {
    try {
      const response = await axios.get(`${this.url}/location/`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async saveWidget(locationId: string, reviews: GoogleReview[], config: WidgetConfig) {
    try {
      const response = await axios.post(
        `${this.url}/widget/save`,
        { location_id: locationId, reviews: JSON.stringify(reviews), config: config },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getWidgetByLocationID(locationId: string) {
    try {
      const response = await axios.get(`${this.url}/widget/${locationId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async taggingImage(
    dataImageURI: string
  ): Promise<{ tags: string[]; description: string }> {
    try {
      const response = await axios.post(`${this.url}/studio/image-captioning`, {
        dataImageURI,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getGooglePhotoAlbum(albumId: string): Promise<GooglePhotoAlbum> {
    try {
      const response = await axios.get(`${this.url}/studio/album/${albumId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getStudioPhotosByLocation(locationId: string) {
    try {
      const response = await axios.get(`${this.url}/studio/${locationId}/photos`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getFacebookProfile(): Promise<any> {
    try {
      const response = await axios.get(`${this.url}/users/facebook/`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getFacebookPages(): Promise<any> {
    try {
      const response = await axios.get(`${this.url}/users/facebook/pages`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getLocationFacebookSelectedPage(): Promise<FacebookPage> {
    try {
      const response = await axios.get(`${this.url}/location/facebook`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async selectFacebookPage(page: FacebookPage): Promise<any> {
    try {
      const response = await axios.post(`${this.url}/location/facebook/pages`, page, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async unselectFacebookPage(): Promise<any> {
    try {
      const response = await axios.delete(`${this.url}/location/facebook`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getLocationSquareMerchantInfo(): Promise<Merchant> {
    try {
      const response = await axios.get(`${this.url}/location/square`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async logoutFacebook(): Promise<void | undefined> {
    try {
      const response = await axios.post(
        `${this.url}/auth/facebook/logout`,
        {},
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async unintegrateSquareFromLocation(): Promise<void | undefined> {
    try {
      const response = await axios.delete(`${this.url}/location/square/`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async removeImagesFromAlbum(
    locationId: string,
    mediaItemIds: string[]
  ): Promise<void | undefined> {
    try {
      const response = await axios.post(
        `${this.url}/studio/${locationId}/photos/delete`,
        {
          mediaItemIds,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async uploadImagesToGooglePhoto(
    locationId: string,
    fileImageItems: FileImageItem[]
  ): Promise<void | undefined> {
    try {
      const response = await axios.post(
        `${this.url}/studio/${locationId}/photos`,
        {
          fileImageItems,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async updateStudioPhoto(
    mediaId: string,
    labels: string[],
    description: string
  ): Promise<StudioPhoto | undefined> {
    try {
      const response = await axios.put(
        `
      ${this.url}/studio/photos/${mediaId}`,
        {
          labels,
          description,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getStudioPhotoByMediaId(
    locationId: string,
    mediaId: string
  ): Promise<StudioPhoto | null> {
    try {
      const response = await axios.get(`${this.url}/studio/photos/${mediaId}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getLocationSocialPosts(locationId: string): Promise<SocialPost[]> {
    try {
      const response = await axios.get(`${this.url}/studio/${locationId}/posts`, {
        withCredentials: true,
      });
      return response.data || [];
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getSocialPostById(socialPostId: number): Promise<SocialPost> {
    try {
      const response = await axios.get(`${this.url}/studio/posts/${socialPostId}`, {
        withCredentials: true,
      });
      return response.data || undefined;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async removeSocialPost(socialPostId: number): Promise<void> {
    try {
      await axios.delete(`${this.url}/studio/posts/${socialPostId}`, {
        withCredentials: true,
      });
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async createSocialPost(mediaItemId: string, content: string): Promise<SocialPost> {
    try {
      const response = await axios.put(
        `${this.url}/studio/posts`,
        {
          mediaItemId,
          content,
        },
        {
          withCredentials: true,
        }
      );
      return response.data || undefined;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async updateSocialPost(
    socialPostId: number,
    date: number,
    content: string
  ): Promise<void> {
    try {
      const response = await axios.put(
        `${this.url}/studio/posts/${socialPostId}`,
        {
          date,
          content,
        },
        {
          withCredentials: true,
        }
      );
      return response.data || [];
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async generateSocialPostContent(socialPostId: number): Promise<string> {
    try {
      const response = await axios.post(
        `${this.url}/studio/posts/${socialPostId}/generate`,
        {},
        {
          withCredentials: true,
        }
      );
      return response.data || '';
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async squareOauthLogin(): Promise<string> {
    try {
      const response = await axios.get(`${this.url}/auth/square/login`, {
        withCredentials: true,
      });
      return response.data || undefined;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  // Inquiry method
  public async updateDefendResponse(locationId: string, defendResponseType: DefendResponseType) {
    try {
      const response = await axios.put(
        `${this.url}/location/${locationId}/defend`,
        {
          defendResponseType,
        },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async openInquiryEmail(inquiryId: string) {
    try {
      const response = await axios.post(`${this.url}/inquiry/${inquiryId}/open`, {});
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async respondInquiry(inquiryId: string, email: string, phone: string | undefined) {
    try {
      const response = await axios.post(`${this.url}/inquiry/${inquiryId}/respond`, {
        email,
        phone,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getInquiryById(inquiryId: string): Promise<Inquiry> {
    try {
      const response = await axios.get(`${this.url}/inquiry/${inquiryId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async getAllInquiries(locationId: string) {
    try {
      const response = await axios.get(`${this.url}/inquiry/locations/${locationId}/`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async batchCreateInquiries(locationId: string, reviewIds: string[]) {
    try {
      const response = await axios.post(
        `${this.url}/inquiry/locations/${locationId}/batchCreate`,
        { reviewIds },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async approveReviewResponse(googleReviewId: string) {
    try {
      const response = await axios.post(`${this.url}/reviews/reply/${googleReviewId}/approve`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async createCheckoutSession(noLicenses: number): Promise<string | undefined> {
    try {
      const response = await axios.post(
        `${this.url}/subscription/createCheckoutSession`,
        { noLicenses },
        {
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  public async createCustomerPortalSession(): Promise<string | undefined> {
    try {
      const response = await axios.post(
        `${this.url}/subscription/createCustomerPortalSession`,
        {},
        { withCredentials: true }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error);
    }
  }
}
